import { Power1 } from 'gsap';
import * as PIXI from 'pixi.js';
import Text from '../../graphics/Text';
import DraggablePreset from './DraggablePreset';
import { NONE } from '../../constants/DropShadow';
import GraphicMask from '../../graphics/GraphicMask';
import {
  getAlignX,
  getFixAboveY,
  getFixBelowY,
  getFixBottomY,
  getFixTopY,
} from '../../utils/CoordinateCalculator';

const MAX_DURATION = 2;

export default class LowerThirds extends DraggablePreset {
  constructor(app) {
    super(app);

    this.topTextMask = new GraphicMask();
    this.topText = new Text(app);
    this.topText.mask = this.topTextMask.mask;

    this.bottomTextMask = new GraphicMask();
    this.bottomText = new Text(app);
    this.bottomText.mask = this.bottomTextMask.mask;

    this.container.addChild(
      this.topText,
      this.bottomText,
      this.topText.mask,
      this.bottomText.mask
    );
  }

  update(model) {
    model = { ...model, dropShadow: NONE }; // disable drop shadow for this preset
    super.update(model);
    const halfDuration = Math.min(this.duration, MAX_DURATION) / 2;
    const topDuration = halfDuration / 2;
    const bottomDuration = halfDuration / 2;

    // top text content and style
    this.topText.text = this.getTextLine(0);
    this.topText.updateStyle(model, {
      fontWeight: 'bold',
    });

    // bottom text content and style
    this.bottomText.text = this.getTextLine(1);
    this.bottomText.updateStyle(model, {
      fill: this.secondaryColor,
      fontWeight: 'bold',
      scale: 0.5,
    });

    this.setDraggableRoot(
      this.topText.innerControls.height + this.bottomText.innerControls.height,
      Math.max(
        this.topText.innerControls.width,
        this.bottomText.innerControls.width
      )
    );

    // base everything else off or root
    this.topText.innerControls.x = getAlignX(
      this.topText.innerControls,
      this.root
    );
    this.bottomText.innerControls.x = getAlignX(
      this.bottomText.innerControls,
      this.root
    );
    this.topText.innerControls.y = getFixTopY(
      this.topText.innerControls,
      this.root
    );
    this.bottomText.innerControls.y = getFixBottomY(
      this.bottomText.innerControls,
      this.root
    );

    // Mask the text where its at
    this.setMaskOnText(this.topText, this.topTextMask);
    this.setMaskOnText(this.bottomText, this.bottomTextMask);

    // initial values to make sure animation works on startup
    this.topText.innerControls.y = getFixBelowY(
      this.topText.innerControls,
      this.root
    );
    this.bottomText.innerControls.y = getFixAboveY(
      this.bottomText.innerControls,
      this.root
    );

    // pop up top text
    this.animate(this.topText.innerControls)
      .to({
        y: getFixTopY(this.topText.innerControls, this.root),
        duration: topDuration,
        ease: Power1.easeOut,
      })
      .reverseOut(this.duration - topDuration - bottomDuration);

    // drop down bottom text
    this.animate(this.bottomText.innerControls)
      .to(
        {
          y: getFixBottomY(this.bottomText.innerControls, this.root),
          duration: bottomDuration,
          ease: Power1.easeOut,
        },
        topDuration
      )
      .reverseOut(this.duration - bottomDuration);
  }

  render() {
    super.render();
    this.topTextMask.render();
    this.bottomTextMask.render();
  }
}
